import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import useTranslate from "@default/hooks/use-translate";

import useConfigQuery from "../hooks/use-config-query/use-config-query";
import LoadingIndicator from "../general-components/loading-indicator";
import Error from "../system/error";
import LogoButton from "../general-components/logo-button";
import MenuCtaNav from "./menu-cta-nav";
import AccessibilityMenu from "./accessibility-menu";
import {
  mainMenuOpenAction,
  accessibilityMenuOpenAction,
  mainSettingsAction,
  releaseStateAction,
} from "../app-actions";
import { setReleaseState } from "@default/lib/setReleaseState";
import { waitForElm } from "@default/lib/wait-for-elm";
import { useLocation } from "react-router-dom";
import ViewSwitch from "@default/paragraphs/overview/components/main-controls/view-switch";
import CustomAnimation from "@default/general-components/custom-animation";
import { isSafari } from "react-device-detect";
import classNames from "classnames";
import LanguageSwitch from "@default/js/i18n/language-switch";
import DashedBorder from "@default/general-components/dashed-border";
import LanguageAndAccessibility from "./language-and-accessibility";
import SupportersHeader from "@default/general-components/supporters/supporters-header";
import ErrorBoundary from "../error-boundary";

const SimpleNavigation = (props) => {
  //@todo: split this into several components and use ContextAPI.

  const currentLanguage = useSelector(
    (reduxStore) => reduxStore.i18n.currentLanguage
  );
  const microSite = useSelector((reduxStore) => reduxStore.appStore.microSite);

  const mainSettings = useSelector(
    (reduxStore) => reduxStore.appStore.mainSettings
  );

  const location = useLocation();

  const isConferenceOverviewPage = useSelector(
    (reduxStore) => reduxStore.appStore.isConferenceOverviewPage
  );

  const isMobile = useSelector((reduxStore) => reduxStore.appStore.isMobile);
  const timetableBreakpoint = useSelector(
    (reduxStore) => reduxStore.appStore.timetableBreakpoint
  );
  const releaseState = useSelector(
    (reduxStore) => reduxStore.appStore.releaseState
  );
  const mainMenuOpen = useSelector(
    (reduxStore) => reduxStore.appStore.mainMenuOpen
  );
  const accessibilityMenuOpen = useSelector(
    (reduxStore) => reduxStore.appStore.accessibilityMenuOpen
  );
  const persistViewSwitch = useSelector(
    (reduxStore) => reduxStore.appStore.persistViewSwitch
  );

  const dispatch = useDispatch();

  const _t = useTranslate();

  const nav = useRef(),
    menuCTA = useRef(),
    problematicBlurEffect = useRef();

  const { data: configData, error } = useConfigQuery(
    "main_settings",
    currentLanguage
  );

  useEffect(() => {
    if (mainSettings) {
      // Get Release State from Main Settings and set the State depending on selected Option
      dispatch(releaseStateAction(setReleaseState(mainSettings)));
    }

    if (!configData || microSite) return;
    dispatch(
      mainSettingsAction(configData?.items ? configData.items[0] : configData)
    );
  }, [configData, mainSettings]);

  const ctaScroll = () => {
    if (isMobile && menuCTA.current) {
      if (
        document.body.getBoundingClientRect().top * -1 >
        window.innerHeight * 1.5
      ) {
        menuCTA?.current?.classList.add("show");
      } else {
        menuCTA?.current?.classList.remove("show");
      }
    }
  };

  useEffect(() => {
    // Close Menus on route change
    if (mainMenuOpen || accessibilityMenuOpen) {
      dispatch(mainMenuOpenAction(false));
      dispatch(accessibilityMenuOpenAction(false));
    }
  }, [location]);

  useEffect(() => {
    setTimeout(() => {
      problematicBlurEffect.current?.classList.add("blur-fix");
    }, 100);

    // Determine Menu Height for a smooth transition
    if (microSite === "rbx-company") {
      waitForElm("#overlay-menu ul", nav.current).then((elm) => {
        document.body.style.setProperty(
          "--overlay-menu-height",
          elm.getBoundingClientRect().height + "px"
        );
      });
    }
  }, []);

  useEffect(() => {
    if (mainSettings?.fieldMenuCta) {
      window.addEventListener("scroll", ctaScroll);
    }
  }, [mainSettings?.fieldMenuCta]);

  if (error) {
    return <Error error={error} />;
  }

  const toggleOverlayMenu = (openIt) => {
    switch (typeof openIt) {
      case "boolean":
        // If a boolean is passed, use it to set the state.
        break;
      case "undefined":
        // If no argument is passed, toggle the current state.
        openIt = !mainMenuOpen;
        break;
      default:
        console.error(
          'toggleOverlayMenu called with unexpected type: "%s": %o',
          typeof openIt,
          openIt
        );
        return;
    }
    dispatch(mainMenuOpenAction(openIt));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === "Escape") {
      toggleOverlayMenu();
    }
  };

  return (
    <>
      <nav
        className={`header-element ${mainMenuOpen ? "active" : ""}`}
        language={currentLanguage}
        ref={nav}
        aria-labelledby={_t("accessibility.main_menu")}
        data-timetable-breakpoint={timetableBreakpoint}
      >
        <span className={"sr-only"} id={_t("accessibility.main_menu")} hidden />
        <div className="container">
          <div
            className={
              microSite ? "row wrapper" : "wrapper d-flex align-items-stretch"
            }
          >
            {!microSite ? (
              <div className="panic-button navigation-element">
                <LogoButton />
              </div>
            ) : (
              <LanguageSwitch />
            )}

            <div className="wrapper flex-grow-1 justify-content-end">
              {!isMobile && (
                <ErrorBoundary>
                  <SupportersHeader />
                </ErrorBoundary>
              )}

              {microSite && <AccessibilityMenu />}
              {!microSite && mainSettings?.fieldMenuCta && !isMobile && (
                <MenuCtaNav />
              )}
              {!microSite && !isMobile && <LanguageAndAccessibility />}
              <div className="button-wrapper navigation-element">
                <DashedBorder blurredBackdrop reverse />
                {/* Menu Toggle */}
                <button
                  id="toggle-overlay-menu"
                  className={mainMenuOpen ? "active" : ""}
                  aria-label={
                    mainMenuOpen
                      ? `${_t("accessibility.main_menu")} ${_t(
                          "accessibility.close"
                        )}`
                      : `${_t("accessibility.main_menu")} ${_t(
                          "accessibility.open"
                        )}`
                  }
                  onClick={() => toggleOverlayMenu()}
                  onKeyDown={handleKeyDown}
                >
                  <div className="bar" />
                  <div className="bar" />
                  {microSite !== "rbx-company" && <div className="bar" />}
                </button>
              </div>
            </div>
          </div>
          {!timetableBreakpoint &&
            (isConferenceOverviewPage || persistViewSwitch) && (
              <div className="mobile-helper row">
                <ViewSwitch />
                {/*<ErrorBoundary>
                  <SupportersHeader />
                </ErrorBoundary>*/}
              </div>
            )}
        </div>
      </nav>
    </>
  );
};

SimpleNavigation.propTypes = {
  showSearch: PropTypes.bool,
  menuTitle: PropTypes.string,
};

export default SimpleNavigation;
